import { defineMessages, useIntl } from 'react-intl-next';
import React from 'react';

import Button from '@atlaskit/button';
import Tooltip from '@atlaskit/tooltip';

import { Logo as AILogo } from '@atlassian/editor-plugin-ai/AILogo';
import { SuggestTitleEvents } from '@atlassian/editor-plugin-ai/ConfluenceSuggestTitleEventEnums';

import type { FloatingToolbarButtonStyleType } from './';

type SuggestTitleButtonProps = {
	floatingToolbarButtonStyle: FloatingToolbarButtonStyleType;
	isEditorEmpty?: boolean;
};

const i18n = defineMessages({
	suggestTitleLabel: {
		id: 'title-area-content-components.toolbar-above-title.suggest-title-button.label',
		description: 'Text in the button to prompt user to generate a title using Editor AI',
		defaultMessage: 'Suggest title',
	},
	suggestTitleTooltipWhenDocEmpty: {
		id: 'title-area-content-components.toolbar-above-title.suggest-title-button.empty-document.tooltip',
		description:
			'Text in the tooltip which appears on a disabled button to advise user that page content is required',
		defaultMessage: 'Page cannot be empty',
	},
});

/**
 * EXPERIMENTAL: Util component for experimental flow where 'Suggest title' in
 * Confluence title toolbar will trigger the 'Suggest a title' flow in Editor AI.
 */
export const SuggestTitleButton = ({
	floatingToolbarButtonStyle,
	isEditorEmpty,
}: SuggestTitleButtonProps) => {
	const intl = useIntl();
	const handleClick = () => {
		const customEvent = new CustomEvent(SuggestTitleEvents.GenerateSuggestedTitle);
		document.dispatchEvent(customEvent);
	};

	const isDisabled = Boolean(isEditorEmpty);

	const iconStyles = {
		marginRight: '1px',
	};

	const button = (
		<Button
			isDisabled={isDisabled}
			appearance="subtle"
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				...floatingToolbarButtonStyle,
			}}
			onClick={handleClick}
			iconBefore={
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				<AILogo isPadded={false} isDisabled={isDisabled} alt="" size={14} style={iconStyles} />
			}
		>
			{intl.formatMessage(i18n.suggestTitleLabel)}
		</Button>
	);

	return isDisabled ? (
		<Tooltip
			content={intl.formatMessage(i18n.suggestTitleTooltipWhenDocEmpty)}
			position="top-start"
		>
			{button}
		</Tooltip>
	) : (
		button
	);
};
